import { useQuery } from '@tanstack/react-query';
import ms from 'ms';
import { type ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ReactSelectInput } from '@hivebrite/design-system/Inputs';

import { useCurrentNetwork } from 'hb-react/shared/contexts';
import { axiosWithSyntaxHandlers } from 'hb-react/shared/utils/axios';

const ReactSelectEvents = ({
  id,
  name,
  ...props
}: ComponentProps<typeof ReactSelectInput>) => {
  const {
    currentNetwork: { id: networkId },
  } = useCurrentNetwork();

  const [inputValue, setInputValue] = useState('');

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events', networkId, inputValue],
    queryFn: () =>
      axiosWithSyntaxHandlers
        .get(Routes.backoffice_network_events_path(networkId), {
          params: {
            fulltext: inputValue,
            format: 'json',
            per_page: 25,
            query: {
              networks: true,
            },
          },
        })
        .then((res) => res.data.events),
    staleTime: ms('5m'),
  });

  const onInputChange = useDebouncedCallback((value) => {
    setInputValue(value);
  }, 500);

  const options = useMemo(() => {
    const combinedEvents = [
      ...(events || []),
      ...(Array.isArray(props.value) ? props.value : [props.value]),
    ];
    const uniqueEventsMap = new Map();

    combinedEvents.forEach((event) => {
      if (event && typeof event.id !== 'undefined') {
        uniqueEventsMap.set(event.id, event);
      }
    });

    return Array.from(uniqueEventsMap.values()).map(({ id, title }) => ({
      value: id,
      label: title,
    }));
  }, [events, props.value]);

  return (
    <ReactSelectInput
      placeholder={__('Search and select events')}
      isMulti
      {...props}
      cacheOptions
      id={id}
      name={name}
      options={options}
      isLoading={isLoading}
      // @ts-expect-error react-select is not typed
      onInputChange={onInputChange}
    />
  );
};

export default ReactSelectEvents;
